import SEO from "@/components/SEO";
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";

interface PublicProfile {
  id: string;
  nome: string;
  sobrenome: string | null;
  avatar_url: string | null;
  bio: string | null;
  instagram: string | null;
  youtube: string | null;
  spotify: string | null;
  tiktok: string | null;
  data_nascimento: string | null;
  mostrar_whatsapp: boolean;
  telefone: string | null;
  email_contato: string | null;
}

interface UserPost {
  id: string;
  user_id: string;
  content: string;
  media_url: string | null;
  media_type: string | null;
  file_name: string | null;
  created_at: string;
}

export default function PerfilPublicoPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [posts, setPosts] = useState<UserPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"sobre" | "publicacoes">("sobre");
  const [postBusca, setPostBusca] = useState("");

  const schema = profile
    ? {
        "@context": "https://schema.org",
        "@type": "ProfilePage",
        name: `${profile.nome} ${profile.sobrenome || ""} | GodoyProd Comunidade`,
        url: `${siteUrl}/comunidade/perfil/${userId}`,
        description: profile.bio || `Perfil público de ${profile.nome} na comunidade GodoyProd.`,
        inLanguage: "pt-BR",
        mainEntity: {
          "@type": "Person",
          name: `${profile.nome} ${profile.sobrenome || ""}`.trim(),
          url: `${siteUrl}/comunidade/perfil/${userId}`,
          ...(profile.instagram && { sameAs: [profile.instagram] }),
        },
      }
    : undefined;

  useEffect(() => {
    if (!userId) {
      navigate("/comunidade");
      return;
    }
    carregarPerfil();
  }, [userId]);

  async function carregarPerfil() {
    setLoading(true);
    const { data, error } = await supabase
      .from("profiles")
      .select("id, nome, sobrenome, avatar_url, bio, instagram, youtube, spotify, tiktok, data_nascimento, mostrar_whatsapp, telefone, email_contato, perfil_publico")
      .eq("id", userId)
      .maybeSingle();

    if (error || !data) {
      setErro("Perfil não encontrado.");
    } else if (!data.perfil_publico) {
      setErro("Este usuário optou por manter o perfil privado.");
    } else {
      setProfile(data);
      carregarPosts(data.id);
    }
    setLoading(false);
  }

  async function carregarPosts(uid: string) {
    setPostsLoading(true);
    const { data } = await supabase
      .from("community_posts")
      .select("id, user_id, content, media_url, media_type, file_name, created_at")
      .eq("user_id", uid)
      .order("created_at", { ascending: false });
    setPosts(data || []);
    setPostsLoading(false);
  }

  function calcularIdade(dataNasc: string | null): number | null {
    if (!dataNasc) return null;
    const nasc = new Date(dataNasc);
    const hoje = new Date();
    let idade = hoje.getFullYear() - nasc.getFullYear();
    const m = hoje.getMonth() - nasc.getMonth();
    if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) idade--;
    return idade;
  }

  function formatDate(dateStr: string) {
    const d = new Date(dateStr);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  }

  const isOwnProfile = user?.id === userId;

  const postsFiltrados = posts.filter((p) =>
    !postBusca.trim() || (p.content || "").toLowerCase().includes(postBusca.toLowerCase())
  );

  return (
    <>
      {schema && (
        <SEO
          title={`${profile?.nome || "Perfil"} - Comunidade GodoyProd`}
          description={`Perfil público de ${profile?.nome || ""} na comunidade GodoyProd. ${profile?.bio || ""}`}
          keywords="perfil músico gospel, comunidade GodoyProd, músicos gospel, partituras gospel"
          schema={schema}
          canonicalPath={`/comunidade/perfil/${userId}`}
        />
      )}
      <div className="min-h-screen bg-godoy-black">
        <Navbar />

        <section className="pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
              <Link
                to="/comunidade"
                className="inline-flex items-center gap-2 text-godoy-lightgray text-sm hover:text-godoy-gold transition-colors"
              >
                <i className="ri-arrow-left-line" /> Voltar para Comunidade
              </Link>
              {isOwnProfile && (
                <Link
                  to="/meu-perfil"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-godoy-gold/10 border border-godoy-gold/40 text-godoy-gold rounded-md text-sm hover:bg-godoy-gold/20 transition-all"
                >
                  <i className="ri-edit-line" />
                  Editar Perfil
                </Link>
              )}
            </div>

            {erro && (
              <div className="p-6 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm text-center mb-8">
                {erro}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-godoy-gold border-t-transparent rounded-full animate-spin" />
              </div>
            ) : profile ? (
              <>
                {/* Card do perfil */}
                <div className="bg-godoy-charcoal/60 border border-godoy-gray/20 rounded-xl p-8 md:p-10 mb-6">
                  <div className="flex items-center gap-5 mb-6">
                    <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-godoy-gold/30 flex-shrink-0 bg-godoy-charcoal">
                      {profile.avatar_url ? (
                        <img src={profile.avatar_url} alt={profile.nome} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-user-line text-4xl text-godoy-lightgray" />
                        </div>
                      )}
                    </div>
                    <div>
                      <h1 className="font-serif text-2xl md:text-3xl font-bold text-godoy-cream">
                        {profile.nome} {profile.sobrenome || ""}
                      </h1>
                      {calcularIdade(profile.data_nascimento) !== null && (
                        <p className="text-godoy-lightgray text-sm mt-1">
                          {calcularIdade(profile.data_nascimento)} anos
                        </p>
                      )}
                      {profile.email_contato && (
                        <a
                          href={`mailto:${profile.email_contato}`}
                          className="text-godoy-gold text-sm hover:underline mt-1 inline-flex items-center gap-1.5"
                        >
                          <i className="ri-mail-line" />
                          {profile.email_contato}
                        </a>
                      )}
                      {/* Badges de redes */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        {profile.instagram && (
                          <a href={profile.instagram} target="_blank" rel="noopener noreferrer"
                            className="w-8 h-8 flex items-center justify-center rounded-md bg-godoy-black/40 text-godoy-lightgray hover:text-godoy-gold hover:bg-godoy-gold/10 transition-all"
                            title="Instagram">
                            <i className="ri-instagram-line text-sm" />
                          </a>
                        )}
                        {profile.youtube && (
                          <a href={profile.youtube} target="_blank" rel="noopener noreferrer"
                            className="w-8 h-8 flex items-center justify-center rounded-md bg-godoy-black/40 text-godoy-lightgray hover:text-red-400 hover:bg-red-500/10 transition-all"
                            title="YouTube">
                            <i className="ri-youtube-line text-sm" />
                          </a>
                        )}
                        {profile.spotify && (
                          <a href={profile.spotify} target="_blank" rel="noopener noreferrer"
                            className="w-8 h-8 flex items-center justify-center rounded-md bg-godoy-black/40 text-godoy-lightgray hover:text-emerald-400 hover:bg-emerald-500/10 transition-all"
                            title="Spotify">
                            <i className="ri-spotify-line text-sm" />
                          </a>
                        )}
                        {profile.tiktok && (
                          <a href={profile.tiktok} target="_blank" rel="noopener noreferrer"
                            className="w-8 h-8 flex items-center justify-center rounded-md bg-godoy-black/40 text-godoy-lightgray hover:text-white hover:bg-black/40 transition-all"
                            title="TikTok">
                            <i className="ri-tiktok-line text-sm" />
                          </a>
                        )}
                        {profile.mostrar_whatsapp && profile.telefone && (
                          <a href={`https://wa.me/${profile.telefone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer"
                            className="w-8 h-8 flex items-center justify-center rounded-md bg-godoy-black/40 text-godoy-lightgray hover:text-emerald-400 hover:bg-emerald-500/10 transition-all"
                            title="WhatsApp">
                            <i className="ri-whatsapp-line text-sm" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  {profile.bio && (
                    <p className="text-godoy-lightgray text-sm leading-relaxed whitespace-pre-line">{profile.bio}</p>
                  )}
                </div>

                {/* Abas: Sobre / Publicações */}
                <div className="flex items-center gap-1 mb-6 border-b border-godoy-gray/20">
                  <button
                    onClick={() => setActiveTab("sobre")}
                    className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                      activeTab === "sobre"
                        ? "text-godoy-gold border-godoy-gold"
                        : "text-godoy-lightgray border-transparent hover:text-godoy-cream"
                    }`}
                  >
                    <i className="ri-user-line mr-1.5" />
                    Sobre
                  </button>
                  <button
                    onClick={() => setActiveTab("publicacoes")}
                    className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                      activeTab === "publicacoes"
                        ? "text-godoy-gold border-godoy-gold"
                        : "text-godoy-lightgray border-transparent hover:text-godoy-cream"
                    }`}
                  >
                    <i className="ri-article-line mr-1.5" />
                    Publicações
                    {posts.length > 0 && (
                      <span className="ml-1.5 text-xs bg-godoy-gold/20 text-godoy-gold px-1.5 py-0.5 rounded-full">
                        {posts.length}
                      </span>
                    )}
                  </button>
                </div>

                {/* Aba Sobre */}
                {activeTab === "sobre" && (
                  <div className="bg-godoy-charcoal/60 border border-godoy-gray/20 rounded-xl p-8">
                    <h2 className="font-serif text-lg font-semibold text-godoy-gold mb-4">Redes Sociais</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {profile.instagram && (
                        <a href={profile.instagram} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-godoy-black/40 rounded-md hover:bg-godoy-gold/10 hover:border-godoy-gold/30 border border-transparent transition-all">
                          <i className="ri-instagram-line text-godoy-gold text-lg" />
                          <div className="min-w-0">
                            <p className="text-godoy-cream text-sm font-medium">Instagram</p>
                            <p className="text-godoy-lightgray/60 text-xs truncate">{profile.instagram}</p>
                          </div>
                        </a>
                      )}
                      {profile.youtube && (
                        <a href={profile.youtube} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-godoy-black/40 rounded-md hover:bg-red-500/10 hover:border-red-500/20 border border-transparent transition-all">
                          <i className="ri-youtube-line text-red-400 text-lg" />
                          <div className="min-w-0">
                            <p className="text-godoy-cream text-sm font-medium">YouTube</p>
                            <p className="text-godoy-lightgray/60 text-xs truncate">{profile.youtube}</p>
                          </div>
                        </a>
                      )}
                      {profile.spotify && (
                        <a href={profile.spotify} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-godoy-black/40 rounded-md hover:bg-emerald-500/10 hover:border-emerald-500/20 border border-transparent transition-all">
                          <i className="ri-spotify-line text-emerald-400 text-lg" />
                          <div className="min-w-0">
                            <p className="text-godoy-cream text-sm font-medium">Spotify</p>
                            <p className="text-godoy-lightgray/60 text-xs truncate">{profile.spotify}</p>
                          </div>
                        </a>
                      )}
                      {profile.tiktok && (
                        <a href={profile.tiktok} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-godoy-black/40 rounded-md hover:bg-white/5 hover:border-white/10 border border-transparent transition-all">
                          <i className="ri-tiktok-line text-godoy-cream text-lg" />
                          <div className="min-w-0">
                            <p className="text-godoy-cream text-sm font-medium">TikTok</p>
                            <p className="text-godoy-lightgray/60 text-xs truncate">{profile.tiktok}</p>
                          </div>
                        </a>
                      )}
                      {profile.mostrar_whatsapp && profile.telefone && (
                        <a href={`https://wa.me/${profile.telefone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-godoy-black/40 rounded-md hover:bg-emerald-500/10 hover:border-emerald-500/20 border border-transparent transition-all">
                          <i className="ri-whatsapp-line text-emerald-400 text-lg" />
                          <div className="min-w-0">
                            <p className="text-godoy-cream text-sm font-medium">WhatsApp</p>
                            <p className="text-godoy-lightgray/60 text-xs truncate">{profile.telefone}</p>
                          </div>
                        </a>
                      )}
                    </div>
                    {!profile.instagram && !profile.youtube && !profile.spotify && !profile.tiktok && !(profile.mostrar_whatsapp && profile.telefone) && (
                      <p className="text-godoy-lightgray/60 text-sm">Este usuário ainda não adicionou redes sociais.</p>
                    )}
                  </div>
                )}

                {/* Aba Publicações */}
                {activeTab === "publicacoes" && (
                  <div>
                    {/* Busca nos posts */}
                    <div className="mb-5">
                      <div className="relative">
                        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-godoy-lightgray/50" />
                        <input
                          type="text"
                          value={postBusca}
                          onChange={(e) => setPostBusca(e.target.value)}
                          placeholder="Buscar nas publicações..."
                          className="w-full pl-11 pr-4 py-3 bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                        />
                      </div>
                    </div>

                    {postsLoading ? (
                      <div className="flex items-center justify-center py-10">
                        <div className="w-6 h-6 border-2 border-godoy-gold border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : postsFiltrados.length === 0 ? (
                      <div className="text-center py-12 bg-godoy-charcoal/40 border border-godoy-gray/20 rounded-lg">
                        <i className="ri-article-line text-3xl text-godoy-gold/30 mb-3" />
                        <p className="text-godoy-lightgray text-sm">
                          {postBusca ? "Nenhuma publicação encontrada." : "Nenhuma publicação ainda."}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {postsFiltrados.map((post) => (
                          <div key={post.id} className="bg-godoy-charcoal/60 border border-godoy-gray/20 rounded-lg p-5">
                            <p className="text-godoy-lightgray/50 text-xs mb-3">{formatDate(post.created_at)}</p>
                            {post.content && (
                              <p className="text-godoy-lightgray text-sm leading-relaxed whitespace-pre-line mb-3">
                                {post.content}
                              </p>
                            )}
                            {post.media_url && (post.media_type === "image" || post.media_type === "gif") && (
                              <div className="rounded-lg overflow-hidden border border-godoy-gray/20 mb-3">
                                <img src={post.media_url} alt="mídia" className="w-full max-h-64 object-cover" />
                              </div>
                            )}
                            {post.media_url && post.media_type === "video" && (
                              <video src={post.media_url} controls className="w-full max-h-64 rounded-lg mb-3" />
                            )}
                            {post.media_url && post.media_type === "audio" && (
                              <audio src={post.media_url} controls className="w-full mb-3" />
                            )}
                            {post.media_url && (post.media_type === "pdf" || post.media_type === "file") && (
                              <a
                                href={post.media_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-3 bg-godoy-black/40 rounded-md border border-godoy-gray/20 hover:border-godoy-gold/30 transition-colors mb-3"
                              >
                                <i className={`${post.media_type === "pdf" ? "ri-file-pdf-line text-red-400" : "ri-file-line text-godoy-gold"} text-lg`} />
                                <span className="text-godoy-cream text-sm truncate">{post.file_name || "Arquivo"}</span>
                                <i className="ri-external-link-line text-godoy-lightgray ml-auto" />
                              </a>
                            )}
                            {post.media_url && post.media_type === "link" && (
                              <a
                                href={post.media_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 p-3 bg-godoy-black/40 rounded-md border border-godoy-gray/20 hover:border-godoy-gold/30 transition-colors mb-3"
                              >
                                <i className="ri-links-line text-godoy-gold" />
                                <span className="text-godoy-gold text-sm truncate">{post.media_url}</span>
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {isOwnProfile && (
                      <div className="mt-6 text-center">
                        <Link
                          to="/comunidade/forum"
                          className="inline-flex items-center gap-2 px-6 py-2.5 bg-godoy-gold/10 border border-godoy-gold/30 text-godoy-gold rounded-md text-sm hover:bg-godoy-gold/20 transition-all"
                        >
                          <i className="ri-add-line" />
                          Nova publicação no feed
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : null}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}